import { LitElement, html, css } from 'lit'
import { StoreMixin } from '../store.js'
import { api } from '../api.js'
import { navigate } from '../router.js'

// OAuth consent (§20.5). An agent arrives here from the application asking for
// access — in practice Ziggy, wanting to read their worklog so a support day can
// become a timesheet line.
//
// The page's whole job is to say plainly what is being handed over, to whom, and that
// it can be taken back. It never renders anything the query string claims: the client
// name and the scopes come back from the server, which validates the request first, so
// a crafted link cannot dress itself up as something else.
const SCOPE_TEXT = {
  'worklog.read': 'Read which tickets you worked on, and for how long'
}

class PageAuthorize extends StoreMixin(LitElement) {
  static properties = {
    _request: { state: true },
    _error: { state: true },
    _busy: { state: true }
  }

  static styles = css`
    :host { display: block; font-family: var(--font-sans); color: var(--eggplant-500); }
    .card { max-width: 520px; margin: 48px auto; background: var(--white); border-radius: var(--radius-card);
            box-shadow: var(--shadow-card); padding: 36px; }
    .eyebrow { font-family: var(--font-eyebrow); font-size: 10.5px; font-weight: 700; letter-spacing: 0.1em;
               text-transform: uppercase; color: var(--label-grey); }
    h1 { margin: 10px 0 6px; font-size: 26px; font-weight: 800; letter-spacing: -0.02em; line-height: 1.15; }
    .who { color: var(--neutral-600); font-size: 14.5px; margin-bottom: 24px; }
    ul { list-style: none; padding: 0; margin: 0 0 24px; }
    li { display: flex; gap: 10px; align-items: flex-start; padding: 12px 0; font-size: 15px;
         border-top: 1px solid var(--border-input); }
    li:last-child { border-bottom: 1px solid var(--border-input); }
    .tick { color: var(--sky-500); font-weight: 700; }
    .note { color: var(--neutral-600); font-size: 13.5px; margin-bottom: 24px; }
    .actions { display: flex; gap: 12px; align-items: center; }
    button { font: inherit; font-size: 15px; font-weight: 700; cursor: pointer; height: 46px; padding: 0 22px;
             border: none; border-radius: 0; }
    button.primary { background: var(--eggplant-500); color: var(--white); }
    button.primary:hover { background: var(--sky-700); }
    button.ghost { background: none; color: var(--eggplant-500); box-shadow: inset 0 0 0 1px var(--eggplant-500); }
    button[disabled] { opacity: 0.55; cursor: default; }
    .error { color: #B4232B; font-size: 14.5px; }
    .error a { color: var(--sky-500); cursor: pointer; }
  `

  connectedCallback() {
    super.connectedCallback()
    this._load()
  }

  get _params() {
    return new URLSearchParams(window.location.search)
  }

  async _load() {
    const q = this._params
    try {
      this._request = await api.get(`/oauth/authorization?${new URLSearchParams({
        client_id: q.get('client_id') || '',
        redirect_uri: q.get('redirect_uri') || '',
        ...(q.get('scope') ? { scope: q.get('scope') } : {}),
        ...(q.get('response_type') ? { response_type: q.get('response_type') } : {})
      })}`)
    } catch (err) {
      this._error = err.message || 'This authorization request is not valid.'
    }
  }

  // Both decisions leave the SPA for the client's redirect, so this is a real
  // navigation rather than a router push.
  async _decide(path) {
    this._busy = true
    const q = this._params
    try {
      const { redirectTo } = await api.post(`/oauth/authorization/${path}`, {
        clientId: this._request.client.clientId,
        redirectUri: this._request.redirectUri,
        scope: this._request.scope,
        ...(q.get('state') ? { state: q.get('state') } : {})
      })
      window.location.href = redirectTo
    } catch (err) {
      this._error = err.message || 'Could not complete the request.'
      this._busy = false
    }
  }

  render() {
    if (this._error) {
      return html`
        <div class="card">
          <div class="eyebrow">Authorization</div>
          <h1>This request isn't valid</h1>
          <p class="error">${this._error}</p>
          <p class="note">Start again from the application that sent you here. If it keeps
            failing, the application's registration may need updating.</p>
          <div class="actions">
            <button class="ghost" @click=${() => navigate('/tickets')}>Back to tickets</button>
          </div>
        </div>`
    }
    if (!this._request) return html`<div class="card">Loading…</div>`

    const { user } = this.appState
    const r = this._request
    return html`
      <div class="card">
        <div class="eyebrow">Authorization</div>
        <h1>${r.client.name} wants access to your worklog</h1>
        <div class="who">Signed in as ${user?.fullName || user?.email}</div>
        <ul>
          ${r.scopes.map(s => html`
            <li><span class="tick">✓</span><span>${SCOPE_TEXT[s] || s}</span></li>`)}
        </ul>
        <p class="note">It will see your own work only — the tickets you handled, the job and
          task each one is booked to, and the hours. It cannot read anyone else's worklog, and
          it cannot change anything in Support. You can disconnect it at any time from
          <a @click=${() => navigate('/connections')}>Connected applications</a>.</p>
        <div class="actions">
          <button class="primary" ?disabled=${this._busy} @click=${() => this._decide('approve')}>
            ${this._busy ? 'Connecting…' : `Allow ${r.client.name}`}
          </button>
          <button class="ghost" ?disabled=${this._busy} @click=${() => this._decide('deny')}>Deny</button>
        </div>
      </div>`
  }
}

customElements.define('page-authorize', PageAuthorize)
